import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Layout/Header';
import Sidebar from '../Layout/Sidebar';
import RouteSelector from './RouteSelector';
import DateRangeFilter from './DateRangeFilter';
import FlightCalendar from './FlightCalendar';
import FlightTable from './FlightTable';
import PointsChart from './PointsChart';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [routes, setRoutes] = useState([]);
  const [flights, setFlights] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(start.getDate() + 331);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  });

  const fetchRoutes = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/flights/routes', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch routes');

      const data = await response.json();
      setRoutes(data);

      const params = new URLSearchParams(location.search);
      const origin = params.get('origin');
      const destination = params.get('destination');

      if (
        origin &&
        destination &&
        data.some(
          (route) => route.origin === origin && route.destination === destination
        )
      ) {
        setSelectedRoute({ origin, destination });
      } else if (data.length > 0) {
        setSelectedRoute({
          origin: data[0].origin,
          destination: data[0].destination,
        });
      }
    } catch (err) {
      setError('Failed to load routes. Please try again.');
      console.error('Route fetch error:', err);
    }
  }, [location.search]); // Dependencies for fetchRoutes

  const fetchFlights = useCallback(async () => {
    if (!selectedRoute) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const { origin, destination } = selectedRoute;
      const { startDate, endDate } = dateRange;

      const url = new URL('/api/flights/flights', window.location.origin);
      url.searchParams.append('origin', origin);
      url.searchParams.append('destination', destination);
      url.searchParams.append('startDate', startDate);
      url.searchParams.append('endDate', endDate);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch flights');

      const data = await response.json();
      setFlights(data);
    } catch (err) {
      setError('Failed to load flights. Please try again.');
      console.error('Flight fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedRoute, dateRange]); // Dependencies for fetchFlights

  const fetchStats = useCallback(async () => {
    if (!selectedRoute) return;

    try {
      const token = localStorage.getItem('authToken');
      const { origin, destination } = selectedRoute;
      const { startDate, endDate } = dateRange;

      const url = new URL('/api/flights/stats', window.location.origin);
      url.searchParams.append('origin', origin);
      url.searchParams.append('destination', destination);
      url.searchParams.append('startDate', startDate);
      url.searchParams.append('endDate', endDate);
      url.searchParams.append('groupBy', 'depart_date');

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [selectedRoute, dateRange]); // Dependencies for fetchStats

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetch('/api/auth/verify', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.authenticated) {
          localStorage.removeItem('authToken');
          navigate('/login');
        }
      })
      .catch((err) => {
        console.error('Auth verification error:', err);
        navigate('/login');
      });

    fetchRoutes();
  }, [navigate, fetchRoutes]);

  useEffect(() => {
    if (selectedRoute) {
      fetchFlights();
      fetchStats();
    }
  }, [selectedRoute, dateRange, fetchFlights, fetchStats]);

  const handleRouteChange = (route) => {
    setSelectedRoute(route);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                JetBlue Award Flight Tracker
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <RouteSelector
                  routes={routes}
                  selectedRoute={selectedRoute}
                  onChange={handleRouteChange}
                />
                <DateRangeFilter
                  dateRange={dateRange}
                  onChange={handleDateRangeChange}
                />
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
            {selectedRoute && (
              <>
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Points Overview
                  </h2>
                  <PointsChart stats={stats} loading={loading} />
                </div>
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Award Availability Calendar
                  </h2>
                  <FlightCalendar
                    flights={flights}
                    dateRange={dateRange}
                    loading={loading}
                  />
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Flight Details
                  </h2>
                  <FlightTable flights={flights} loading={loading} />
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;